import { useState } from "react";
import { useForm } from "@mantine/form";
import { z } from "zod";
import { zodResolver } from "mantine-form-zod-resolver";
import { useTranslation } from "react-i18next";
import { IconUserPlus } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Center,
  Grid,
  Paper,
  Select,
  Text,
  TextInput,
} from "@mantine/core";
import { FieldValidation } from "@/components/Elements/Field/FieldValidation";
import { Salutation } from "@/types/enums";
import { getEnumTransKey } from "@/utils/trans";
import { type Schemas } from "@/types";
import { useEntityCreateMutation } from "@/features/entity/mutations";
import validator from "validator";
import i18next from "i18next";

const formSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  salutation: z.enum(Salutation as [string]).nullable(),
  email: z
    .string()
    .min(1, { message: i18next.t("common:validation.emailRequired") })
    .refine(
      (value) => {
        if (value) {
          return validator.isEmail(value);
        }
        return true;
      },
      { message: i18next.t("common:validation.invalidEmail") },
    ),
  mobile: z
    .string()
    .min(1, { message: i18next.t("common:validation.phoneRequired") })
    .refine(
      (value) => {
        if (value) {
          return validator.isMobilePhone(value);
        }
        return true;
      },
      { message: i18next.t("common:validation.invalidPhone") },
    ),
  phone: z.string().refine(
    (value) => {
      if (value) {
        return validator.isMobilePhone(value);
      }
      return true;
    },
    { message: i18next.t("common:validation.invalidPhone") },
  ),
});

export type ContactQuickFormSchema = z.infer<typeof formSchema>;

interface PageContactQuickFormProps {
  contact?: Partial<Schemas["ContactCreateDto"]>;
  closeModal?: () => void;
  onCreated?: (contactId: string) => void;
  fromLookup?: boolean;
}

export default function PageContactQuickForm({
  contact,
  closeModal,
  onCreated,
  fromLookup = false,
}: PageContactQuickFormProps) {
  const { t } = useTranslation("features");
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const form = useForm<ContactQuickFormSchema>({
    initialValues: {
      firstName: contact?.firstName ?? "",
      lastName: contact?.lastName ?? "",
      salutation:
        (contact?.salutation as ContactQuickFormSchema["salutation"]) ??
        "MrMrs",
      email: contact?.email ?? "",
      mobile: contact?.mobile ?? "",
      phone: contact?.phone ?? "",
    },
    validate: zodResolver(formSchema),
  });

  const { mutate } = useEntityCreateMutation<
    Schemas["Contact"],
    Schemas["ContactCreateDto"]
  >({ resourcePath: "/api/Contacts", queryKey: "contact" });

  const handleClose = () => {
    closeModal?.();
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        form.onSubmit((values) => {
          setIsLoading(true);
          mutate(values as Schemas["ContactCreateDto"], {
            onSuccess: (data) => {
              const newId = data.data.id;
              if (newId) {
                onCreated?.(newId);
                if (!fromLookup) {
                  navigate(`/app/contacts/${newId}/general`);
                }
              }
              setIsLoading(false);
              handleClose();
            },
            onError: () => {
              setIsLoading(false);
            },
          });
          form.resetDirty();
        })(event);
      }}
    >
      <Center>
        <Text fz={16} fw={600} c={"#282828"}>
          {t("wizards.QuickAddContact.Title")}
        </Text>
      </Center>
      <Center>
        <Text fz={10} fw={300} c={"#ADADAD"}>
          {t("wizards.QuickAddContact.Label")}
        </Text>
      </Center>
      <Box p={16} mr={"14%"} ml={"14%"}>
        <Grid mt="lg">
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper p="lg" h={"100%"}>
              <FieldValidation isDirty={form.isDirty("firstName")}>
                <TextInput
                  required
                  label={t("contacts.firstName")}
                  {...form.getInputProps("firstName")}
                />
              </FieldValidation>
              <FieldValidation isDirty={form.isDirty("lastName")}>
                <TextInput
                  required
                  label={t("contacts.lastName")}
                  {...form.getInputProps("lastName")}
                />
              </FieldValidation>
              <FieldValidation isDirty={form.isDirty("salutation")}>
                <Select
                  searchable
                  label={t("leads.salutation")}
                  data={Salutation.map((value) => ({
                    value,
                    label: t(getEnumTransKey("leads", value)),
                  }))}
                  clearable
                  {...form.getInputProps("salutation")}
                />
              </FieldValidation>
            </Paper>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper p="lg" h={"100%"}>
              <FieldValidation isDirty={form.isDirty("email")}>
                <TextInput
                  required
                  label={t("contacts.email")}
                  {...form.getInputProps("email")}
                />
              </FieldValidation>
              <FieldValidation isDirty={form.isDirty("mobile")}>
                <TextInput
                  required
                  label={t("contacts.mobile")}
                  {...form.getInputProps("mobile")}
                />
              </FieldValidation>
              <FieldValidation isDirty={form.isDirty("phone")}>
                <TextInput
                  label={t("contacts.phone")}
                  {...form.getInputProps("phone")}
                />
              </FieldValidation>
            </Paper>
          </Grid.Col>
        </Grid>
      </Box>
      <Center mt={24}>
        <Button
          type="submit"
          loading={isLoading}
          leftSection={<IconUserPlus width={20} height={20} />}
          fz={14}
          fw={400}
        >
          {t("wizards.QuickAddContact.ConfirmButton")}
        </Button>
      </Center>
    </form>
  );
}
